<script setup lang="ts">
  import { useSettingsStore } from '@bv/stores/settings';
  import { trans } from '@bv/utils';
  import { storeToRefs } from 'pinia';
  import { ref, reactive, computed, watch, onMounted } from 'vue';

  import notificationsApi from '@/api/notifications/notifications.api';
  import { useNotificationRedirect } from '@/composables/useNotificationRedirect';
  import { useNotificationsStore } from '@/stores/notifications/notifications.store';
  import type { infiniteScrollExpose, uiFlagsType } from '@/types/general';
  import type { notificationType } from '@/types/notifications';
  import NotificationsPopup from '@/views/System/Notifications/Components/NotificationsPopup.vue';

  import NotificationItem from './NotificationItem.vue';

  // Composable
  const { handleRedirect } = useNotificationRedirect();
  // store
  const notificationsStore = useNotificationsStore();
  const { notificationsNumber } = storeToRefs(notificationsStore);
  const { setNotificationsNumber } = notificationsStore;
  const settingsStore = useSettingsStore();

  // state
  const activeTab = ref('new');

  const uiFlags = reactive<Partial<uiFlagsType>>({
    isFetching: false,
    isOpen: false,
  });
  const readRef = ref<infiniteScrollExpose<notificationType> | null>(null);
  const unreadRef = ref<infiniteScrollExpose<notificationType> | null>(null);

  // computed
  const tabs = computed(() => [
    { title: trans('notifications.tabs.labels.new_trans'), value: 'new' },
    { title: trans('notifications.tabs.labels.read_trans'), value: 'read' },
  ]);

  const isNewLayout = computed(() => settingsStore.layoutType === 'new');

  // methods
  const closePanel = () => {
    uiFlags.isOpen = false;
  };

  const toggleNotificationsPanel = () => {
    uiFlags.isOpen = !uiFlags.isOpen;
  };

  const markAsRead = (notificationId: string) => {
    notificationsApi.create({ status: 'read' }, `notifications/${notificationId}/read`).then(() => {
      unreadRef.value?.reset();
      readRef.value?.reset();
      setNotificationsNumber(Math.max(0, (notificationsNumber.value ?? 0) - 1));
    });
  };

  const markAllRead = () => {
    notificationsApi.create({ status: 'read' }, 'notifications/read-all').then(() => {
      readRef.value?.reset();
      unreadRef.value?.reset();
      setNotificationsNumber(0);
    });
  };

  const fetchNotificationsCount = async () => {
    const res = await notificationsApi.get({}, 'notifications?status=unread');
    setNotificationsNumber(res.data.meta.total ?? 0);
  };

  const activeRef = computed(() => (activeTab.value === 'read' ? readRef.value : unreadRef.value));

  const handleNotificationRoute = (notification: notificationType) => {
    markAsRead(notification.id);
    handleRedirect(notification);
    closePanel();
  };

  watch(activeTab, () => {
    activeRef.value?.reset();
  });

  onMounted(() => {
    fetchNotificationsCount();
  });
</script>

<template>
  <div class="relative">
    <bv-btn
      class="relative p-0"
      variant="text"
      @click="toggleNotificationsPanel"
    >
      <v-badge
        v-show="notificationsNumber"
        class="z-10"
        color="warning"
        :content="notificationsNumber"
        position="top-end"
      >
        <bv-icon
          :class="{ 'text-white': !isNewLayout }"
          icon="bvi-bell"
          size="lg"
        />
      </v-badge>
      <bv-icon
        v-if="!notificationsNumber"
        :class="{ 'text-white': !isNewLayout }"
        icon="bvi-bell"
        size="lg"
      />
    </bv-btn>

    <transition
      v-click-outside="closePanel"
      :name="isNewLayout ? 'slide-top' : 'slide-in'"
    >
      <div
        v-if="uiFlags.isOpen"
        class="h-130.5 rounded-xl bg-white shadow-2xl absolute w-80 z-50 flex flex-col"
        :class="[isNewLayout ? 'inset-e-0 mt-3' : 'bottom-[7%] xl:bottom-[4%] inset-s-16']"
      >
        <!-- Header -->
        <div class="p-4 flex justify-between items-center">
          <h2 class="text-sm font-semibold">{{ trans('notifications.title_trans') }}</h2>
          <div class="flex items-center gap-4">
            <bv-btn
              class="p-0 h-auto text-gray-600"
              :to="{ name: 's-notifications' }"
              variant="text"
            >
              <bv-icon icon="bvi-setting" />
            </bv-btn>
            <bv-btn
              class="p-0 h-auto text-gray-600"
              variant="text"
              @click="closePanel"
            >
              <bv-icon icon="bvi-close-circle" />
            </bv-btn>
          </div>
        </div>

        <!-- Notifications List -->
        <div class="flex-1 overflow-y-auto rounded-xl">
          <bv-tabs
            v-model="activeTab"
            class="gap-3 bg-white rounded-lg"
            :tabs="tabs"
          >
            <template
              v-if="activeTab !== 'read'"
              #header-actions
            >
              <bv-btn
                class="h-auto p-1 me-1 text-xs"
                :class="{ hidden: activeTab === 'read' }"
                :disabled="activeTab === 'read'"
                variant="text"
                @click="markAllRead"
              >
                {{ trans('notifications.tabs.buttons.read_all') }}
              </bv-btn>
            </template>
            <template #read>
              <notification-item
                ref="readRef"
                type="read"
                @redirect="handleNotificationRoute"
              />
            </template>
            <template #new>
              <notification-item
                ref="unreadRef"
                type="unread"
                @read="markAsRead"
                @redirect="handleNotificationRoute"
              />
            </template>
          </bv-tabs>
        </div>
      </div>
    </transition>
  </div>

  <notifications-popup />
</template>

<style scoped lang="scss">
  :deep() {
    .bv-infinite-scroll:has(.empty) {
      height: 410px;
    }
    .v-slide-group {
      margin-inline-start: 8px;
    }
  }
  .slide-in-enter-active,
  .slide-in-leave-active,
  .slide-top-enter-active,
  .slide-top-leave-active {
    transition: all 0.4s ease-in-out;
  }

  .slide-in-enter-from,
  .slide-in-leave-to {
    transform: translateX(-100%);
    opacity: 0;
  }

  .slide-top-enter-from,
  .slide-top-leave-to {
    transform: translateY(-100%);
    opacity: 0;
  }
</style>
